import { useEffect, useState } from "react";
import { Package, Send, AlertTriangle, Undo2, ArchiveRestore, Plus, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDashboardCache } from "./hooks/useDashboardCache";
import { DashboardHeader } from "./components/DashboardHeader";
import { QuickActionsRow } from "./components/QuickActionsRow";
import { KpiCard, KpiCardSkeleton } from "./components/KpiCard";
import { NeedsAttention } from "./components/NeedsAttention";
import { api } from "@/lib/api";

/**
 * StorekeeperDashboard — parts-first operational view.
 */
export default function StorekeeperDashboard({ user }) {
  const nav = useNavigate();
  const [parts, setParts] = useState([]);
  const store = useDashboardCache("fender_store_summary", "/dashboard/store-summary");
  const attention = useDashboardCache("fender_store_attention", "/dashboard/action-centre");

  useEffect(() => {
    api.get("/parts").then((r) => setParts(r.data || [])).catch(() => {});
  }, []);

  const s = store.data || {};
  const low = parts.filter(
    (p) => Number(p.stock_qty || 0) <= Number(p.reorder_level || 0) && Number(p.stock_qty || 0) > 0
  ).slice(0, 8);

  const kpis = [
    { key: "requested", label: "Parts Requested", value: s.parts_requested ?? 0, hint: "Awaiting confirmation", Icon: Send, tone: "steel", to: "/enquiries", testId: "store-kpi-requested" },
    { key: "issue", label: "Parts to Issue", value: s.parts_to_issue ?? 0, hint: "Jobs waiting for parts", Icon: Wrench, tone: "orange", to: "/job-cards?status=awaiting_parts", testId: "store-kpi-issue" },
    { key: "low", label: "Low Stock", value: s.low_stock ?? 0, hint: "Reorder soon", Icon: Package, tone: "orange", to: "/inventory", testId: "store-kpi-low" },
    { key: "zero", label: "Out of Stock", value: s.out_of_stock ?? 0, hint: "Immediate reorder", Icon: AlertTriangle, tone: "red", to: "/inventory", testId: "store-kpi-zero" },
    { key: "received", label: "Received Today", value: s.received_today ?? 0, hint: "Fresh stock entries", Icon: ArchiveRestore, tone: "green", testId: "store-kpi-received" },
    { key: "returns", label: "Pending Returns", value: s.pending_returns ?? 0, hint: "Return to supplier", Icon: Undo2, tone: "frosted", testId: "store-kpi-returns" },
  ];

  const quickActions = [
    { key: "issue-stock", label: "Issue Stock", Icon: Send, url: "/inventory", tone: "primary" },
    { key: "receive-stock", label: "Receive Stock", Icon: ArchiveRestore, url: "/inventory" },
    { key: "add-inv", label: "Add Inventory Item", Icon: Plus, url: "/inventory" },
    { key: "adjust", label: "Adjust Stock", Icon: Package, url: "/inventory" },
    { key: "requests", label: "Parts Requests", Icon: Wrench, url: "/enquiries" },
  ];

  const hi = (user?.name || "").split(" ")[0] || "Store";
  return (
    <div className="space-y-6" data-testid="storekeeper-dashboard-v3">
      <DashboardHeader
        title="Parts Room"
        subtitle={`Stock, requests and enquiries at a glance, ${hi}.`}
        range="today"
        onRangeChange={() => {}}
        onRefresh={() => { store.reload(); attention.reload(); }}
        onCustomize={() => {}}
        onFilter={() => {}}
        lastSyncedAt={store.lastSyncedAt}
        loading={store.loading}
      />
      <QuickActionsRow actions={quickActions} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map(({ key, ...rest }) => (store.loading && !store.data ? <KpiCardSkeleton key={key} /> : <KpiCard key={key} {...rest} />))}
      </div>
      <NeedsAttention items={(attention.data?.items || []).filter((it) => ["inventory", "parts"].includes(it.category))} />

      {/* Low stock priority list */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="store-lowstock-list">
        <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Low stock — restock priority</h3>
            <p className="text-[11.5px] text-[#667085] mt-0.5">Parts nearing or below reorder level.</p>
          </div>
          <button
            type="button"
            onClick={() => nav("/inventory")}
            className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946]"
            data-testid="store-lowstock-view-all"
          >
            View inventory →
          </button>
        </header>
        {low.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">No parts need restocking. Good work.</div>
        ) : (
          <ul className="divide-y divide-[#F1FAEE]">
            {low.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-5 py-3" data-testid={`store-low-${p.id}`}>
                <div className="w-8 h-8 rounded-md bg-[#F79009]/10 text-[#F79009] grid place-items-center shrink-0">
                  <Package size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1d3557] truncate">{p.name}</div>
                  <div className="text-[11px] text-[#667085]">SKU {p.sku}</div>
                </div>
                <div className="text-[12px] font-mono text-[#e63946] font-semibold">
                  {p.stock_qty || 0} / {p.reorder_level || 0}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
